import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const EMPTY = {
  name: "", category: "", short_description: "", long_description: "", benefits: "",
  ideal_candidate: "", recovery_time: "", duration: "", anesthesia: "", hospital_stay: "",
  risk_level: "Low", popularity: 50, status: "Active", featured: false, commission_notes: "",
  cover_image: "", gallery: [], before_images: [], after_images: [], tags: [], faqs: [], included: [], not_included: [],
  min_price: 0, avg_price: 0, max_price: 0, offer_price: 0, doctor_fees: 0, hospital_charges: 0,
  implant_cost: 0, consumables: 0, ot_charges: 0, medicine_charges: 0, followup_charges: 0, taxes: 0,
  sales_commission: 0, counsellor_incentive: 0, marketing_commission: 0, doctor_incentive: 0,
  referral_commission: 0, hospital_share: 0, commission_pct: 10,
};

const PRICING_FIELDS = [
  ["min_price", "Minimum Price"], ["avg_price", "Average Price"], ["max_price", "Maximum Price"],
  ["offer_price", "Offer Price"], ["doctor_fees", "Doctor Fees"], ["hospital_charges", "Hospital Charges"],
  ["implant_cost", "Implant Cost"], ["consumables", "Consumables"], ["ot_charges", "OT Charges"],
  ["medicine_charges", "Medicine Charges"], ["followup_charges", "Follow-up Charges"], ["taxes", "Taxes"],
];

const COMMISSION_FIELDS = [
  ["commission_pct", "Commission %"], ["sales_commission", "Sales Executive Commission"],
  ["counsellor_incentive", "Counsellor Incentive"], ["marketing_commission", "Marketing Commission"],
  ["doctor_incentive", "Doctor Incentive"], ["referral_commission", "Referral Commission"],
  ["hospital_share", "Hospital Share"],
];

export default function ProcedureDialog({ open, onOpenChange, procedure, categories, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!procedure;

  useEffect(() => {
    if (procedure) setForm({ ...EMPTY, ...procedure });
    else setForm({ ...EMPTY, category: categories[0]?.name || "" });
  }, [procedure, open, categories]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.category) { toast.error("Name and category are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      PRICING_FIELDS.concat(COMMISSION_FIELDS).forEach(([k]) => { payload[k] = Number(payload[k]) || 0; });
      payload.popularity = Number(payload.popularity) || 0;
      if (isEdit) await api.put(`/procedures/${procedure.id}`, payload);
      else await api.post("/procedures", payload);
      toast.success(isEdit ? "Procedure updated" : "Procedure created");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit Procedure" : "Add New Procedure"}</DialogTitle>
        <DialogDescription>Fill in the procedure information, pricing and commission details.</DialogDescription></DialogHeader>
        <Tabs defaultValue="info">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="info" data-testid="tab-info">Information</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger value="commission" data-testid="tab-commission">Commission</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4 pt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Procedure Name</Label><Input data-testid="proc-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="proc-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Short Description</Label><Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} /></div>
            <div className="space-y-2"><Label>Long Description</Label><Textarea rows={3} value={form.long_description} onChange={(e) => set("long_description", e.target.value)} /></div>
            <div className="space-y-2"><Label>Benefits</Label><Textarea rows={2} value={form.benefits} onChange={(e) => set("benefits", e.target.value)} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cover Image URL</Label><Input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="https://…" /></div>
              <div className="space-y-2"><Label>Ideal Candidate</Label><Input value={form.ideal_candidate} onChange={(e) => set("ideal_candidate", e.target.value)} /></div>
              <div className="space-y-2"><Label>Recovery Time</Label><Input value={form.recovery_time} onChange={(e) => set("recovery_time", e.target.value)} /></div>
              <div className="space-y-2"><Label>Duration</Label><Input value={form.duration} onChange={(e) => set("duration", e.target.value)} /></div>
              <div className="space-y-2"><Label>Anesthesia</Label><Input value={form.anesthesia} onChange={(e) => set("anesthesia", e.target.value)} /></div>
              <div className="space-y-2"><Label>Hospital Stay</Label><Input value={form.hospital_stay} onChange={(e) => set("hospital_stay", e.target.value)} /></div>
              <div className="space-y-2"><Label>Risk Level</Label>
                <Select value={form.risk_level} onValueChange={(v) => set("risk_level", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Moderate", "High"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Popularity (0-100)</Label><Input type="number" value={form.popularity} onChange={(e) => set("popularity", e.target.value)} /></div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="proc-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Active", "Inactive"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="proc-featured" /><Label>Featured procedure</Label></div>
          </TabsContent>

          <TabsContent value="pricing" className="pt-2">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {PRICING_FIELDS.map(([k, label]) => (
                <div key={k} className="space-y-2"><Label>{label} (₹)</Label>
                  <Input type="number" data-testid={`price-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} /></div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="commission" className="pt-2 space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {COMMISSION_FIELDS.map(([k, label]) => (
                <div key={k} className="space-y-2"><Label>{label}{k === "commission_pct" ? " (%)" : " (₹)"}</Label>
                  <Input type="number" data-testid={`comm-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} /></div>
              ))}
            </div>
            <div className="space-y-2"><Label>Commission Notes</Label><Textarea rows={2} value={form.commission_notes} onChange={(e) => set("commission_notes", e.target.value)} /></div>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-procedure-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Procedure"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
